import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import InventoryFilter from '../user/sell/inventory/InventoryFilter';
import { SortOption } from '../user/sell/Sell';
import { closeFilter, openFilter } from '../../store/slices/sidebarFilterSlice';

import MarketplaceGrid from './MarketplaceGrid';
import { useIsDesktop } from "../../hooks/useIsDesktop";
import {defaultFilterState, InventoryFilterState} from '../skin-db/database/filter/SkinDbFilter';

const Marketplace: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();
  const filterCollapsed = useSelector((state: RootState) => state.sidebarFilter.collapsed);

  const { offerId } = useParams<{ offerId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialSearch = searchParams.get('search') || '';

  const [filters, setFilters] = useState<InventoryFilterState>(() => ({
    ...defaultFilterState,
    name: initialSearch,
  }));
  const [sortBy, setSortBy] = useState<SortOption>('price_desc');

  useEffect(() => {
    const searchFromUrl = searchParams.get('search');
    if (searchFromUrl !== null && searchFromUrl !== filters.name) {
      setFilters((prev) => ({
        ...prev,
        name: searchFromUrl,
      }));
    }
  }, [searchParams]);

  useEffect(() => {
    if (isDesktop) {
      dispatch(openFilter());
    } else {
      dispatch(closeFilter());
    }
  }, [isDesktop, dispatch]);

  const handleResetAll = () => {
    // Preserve both the search name and the current sorting option
    setFilters(prev => ({
      ...defaultFilterState,
      name: prev.name
    }));
    if (offerId) navigate('/market'); // Fixed path to match Pages.tsx
  };

  const handleSearchChange = (val: string) => {
    setFilters({ ...filters, name: val });
    if (searchParams.has('search')) {
      if (val) {
        setSearchParams({ search: val }, { replace: true });
      } else {
        setSearchParams({}, { replace: true });
      }
    }
  };

  return (
      <Box className="px-3 md:p-4 mt-20 flex gap-4">

        {/* MOBILE FILTER OVERLAY */}
        {!filterCollapsed && !isDesktop && (
            <div className="lg:hidden fixed left-0 right-0 bottom-0 top-[65px] z-50 bg-[#0d0e11] overflow-y-auto flex justify-center">
              <div className="w-full max-w-md">
                <InventoryFilter
                    filters={filters}
                    onChange={(newFilters) => setFilters(newFilters)}
                    onClose={() => dispatch(closeFilter())}
                    collapsed={false}
                    tradeLockFilterDisabled={true}
                />
              </div>
            </div>
        )}

        {/* DESKTOP SIDEBAR */}
        <div
            className={`hidden lg:block overflow-hidden transition-all duration-300 ease-in-out ${
                filterCollapsed ? 'w-0 opacity-0' : 'w-72 opacity-100'
            }`}
        >
          <InventoryFilter
              filters={filters}
              onChange={(newFilters) => setFilters(newFilters)}
              collapsed={false}
              tradeLockFilterDisabled={true}
              onClose={() => dispatch(closeFilter())}
          />
        </div>

        <div className="flex-1 min-w-0">
          <MarketplaceGrid
              filters={filters}
              sortBy={sortBy}
              searchTerm={filters.name || ''}
              onSearchChange={handleSearchChange}
              onSortChange={setSortBy}
              onResetFilters={handleResetAll}
              onUpdateFilters={setFilters}
              highlightOfferId={offerId}
          />
        </div>
      </Box>
  );
};

export default Marketplace;
